/**
 * The network or funding design a plan is built on. One list, the way Practice
 * Fusion ships it: a plan is only ever one of these, so asking for a network design
 * AND a funding category would demand a second answer that is either implied (a PPO
 * is commercial) or meaningless (an auto PIP plan has no network). The category
 * heading is derived below, never entered.
 *
 * COVERAGE_TYPES is the other axis, not a second answer to this one: which service
 * line the plan pays for. No plan type implies it — a dental PPO and a medical PPO
 * are both PPO — so it is stored and picked per plan rather than derived.
 */
export const PLAN_TYPES = [
  "PPO",
  "HMO",
  "EPO",
  "POS",
  "INDEMNITY",
  "MEDICARE",
  "MEDICARE_ADVANTAGE",
  "MEDICARE_SUPPLEMENT",
  "MEDICAID",
  "MEDICAID_MANAGED_CARE",
  "TRICARE",
  "VA",
  "WORKERS_COMP",
  "AUTO_PIP",
  "AUTO_MED_PAY",
  "MOTOR_VEHICLE",
  "LIABILITY",
  "SELF_PAY",
  "OTHER",
] as const;

export type PlanType = (typeof PLAN_TYPES)[number];

export const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  PPO: "PPO",
  HMO: "HMO",
  EPO: "EPO",
  POS: "POS",
  INDEMNITY: "Indemnity",
  MEDICARE: "Medicare",
  MEDICARE_ADVANTAGE: "Medicare Advantage",
  MEDICARE_SUPPLEMENT: "Medicare Supplement",
  MEDICAID: "Medicaid",
  MEDICAID_MANAGED_CARE: "Medicaid Managed Care",
  TRICARE: "TRICARE",
  VA: "VA",
  WORKERS_COMP: "Workers' Comp",
  // Both vocabularies in one label: PIP is the coverage, no-fault is the system
  // it exists under, and a biller searching either word has to find this row.
  AUTO_PIP: "Auto PIP / No-Fault",
  AUTO_MED_PAY: "Auto Med-Pay",
  MOTOR_VEHICLE: "Motor Vehicle",
  LIABILITY: "Liability",
  SELF_PAY: "Self-pay",
  OTHER: "Other",
};

/**
 * Which service line a plan pays for. Stored on the plan and editable in Settings,
 * because deriving it from plan type gets every dental and vision plan wrong and
 * leaves nobody able to correct one. Nullable — most plans start with it unset.
 *
 * The carrier's payer category does not scope this: service line and payer sector
 * are separate questions, so a workers' comp plan still bills medical services.
 */
export const COVERAGE_TYPES = [
  "MEDICAL",
  "DENTAL",
  "VISION",
  "BEHAVIORAL",
] as const;

export type CoverageType = (typeof COVERAGE_TYPES)[number];

export const COVERAGE_TYPE_LABELS: Record<CoverageType, string> = {
  MEDICAL: "Medical",
  DENTAL: "Dental",
  VISION: "Vision",
  BEHAVIORAL: "Behavioral health",
};

/**
 * Which sector a carrier operates in. Single-valued per carrier where the product
 * never is: health insurance and property/casualty are separately licensed lines
 * written by separate entities, so Chubb's motor-vehicle and workers' comp plans
 * are both P&C and Aetna's commercial, Medicare Advantage and Medicaid plans are
 * all health.
 */
export const PAYER_CATEGORIES = ["HEALTH", "PROPERTY_CASUALTY"] as const;

export type PayerCategory = (typeof PAYER_CATEGORIES)[number];

export const PAYER_CATEGORY_LABELS: Record<PayerCategory, string> = {
  HEALTH: "Health",
  PROPERTY_CASUALTY: "Property & Casualty",
};

/**
 * The carrier's sector scopes its plans' products, so a P&C carrier is never
 * offered a PPO and a health carrier is never offered PIP. Mirrors
 * PLAN_TYPES_BY_CATEGORY on the backend.
 */
export const PLAN_TYPES_BY_CATEGORY: Record<
  PayerCategory,
  Array<{ label: string; types: PlanType[] }>
> = {
  HEALTH: [
    { label: "Commercial", types: ["PPO", "HMO", "EPO", "POS", "INDEMNITY"] },
    {
      label: "Government",
      types: [
        "MEDICARE",
        "MEDICARE_ADVANTAGE",
        "MEDICARE_SUPPLEMENT",
        "MEDICAID",
        "MEDICAID_MANAGED_CARE",
        "TRICARE",
        "VA",
      ],
    },
    { label: "Other", types: ["SELF_PAY", "OTHER"] },
  ],
  PROPERTY_CASUALTY: [
    {
      label: "Injury",
      types: [
        "WORKERS_COMP",
        "AUTO_PIP",
        "AUTO_MED_PAY",
        "MOTOR_VEHICLE",
        "LIABILITY",
      ],
    },
    { label: "Other", types: ["OTHER"] },
  ],
};

/**
 * Every group, for surfaces with no carrier in hand (the catalog filter). Merged by
 * label rather than sliced by index, which dropped types when a group was reordered.
 */
export const ALL_PLAN_TYPE_GROUPS: Array<{
  label: string;
  types: PlanType[];
}> = Object.values(PLAN_TYPES_BY_CATEGORY)
  .flat()
  .reduce<Array<{ label: string; types: PlanType[] }>>((groups, group) => {
    const existing = groups.find((g) => g.label === group.label);
    if (existing) {
      for (const type of group.types) {
        if (!existing.types.includes(type)) existing.types.push(type);
      }
      return groups;
    }
    return [...groups, { label: group.label, types: [...group.types] }];
  }, []);

export interface InsurancePlan {
  id: string;
  payerId: string;
  name: string;
  planType?: PlanType | null;
  coverageType?: CoverageType | null;
  addressLineOne?: string | null;
  addressLineTwo?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  claimsPhone?: string | null;
  notes?: string | null;
  isActive: boolean;
}

export interface InsuranceCarrier {
  id: string;
  name: string;
  code?: string | null;
  tspid?: string | null;
  payerCategory?: PayerCategory | null;
  isInNetwork: boolean;
  isActive: boolean;
  plans: InsurancePlan[];
}

/** isActive is owned by archive/restore, not by the editor form. */
export type InsuranceCarrierCreate = Omit<
  InsuranceCarrier,
  "id" | "plans" | "isActive"
> & { plans: InsurancePlanUpsert[] };

export type InsuranceCarrierUpdate = Partial<InsuranceCarrierCreate>;

export type InsurancePlanCreate = Omit<InsurancePlan, "id" | "payerId">;

export type InsurancePlanUpdate = Partial<InsurancePlanCreate>;

/**
 * One entry in a carrier's desired plan list. A null id means create it; a saved plan
 * left out of the list is archived. Carrier and plans save in a single request so a
 * failure cannot leave the carrier updated and its plans on their old values.
 */
export type InsurancePlanUpsert = InsurancePlanCreate & { id: string | null };
